import { Grid } from '@mui/material'
import { CarrierType, EquipmentType } from '@operations/app/api'
import { useTranslate } from '@tolgee/react'
import { FieldBox, TrainIcon } from '@tom-ui/ui'
import { IFormStore, useRegisterSubFormStore } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { ICarrierVisitAssignment } from '../../models/operational-equipment-assignment.model'
import { CarrierVisitAssignmentSubFormStore } from '../../stores/carrier-visit-assignment.sub-form-store'
import { OperationalEquipmentAdd } from '../atoms/OperationalEquipmentAdd'
import { OperationalEquipmentAssignmentActions } from '../atoms/OperationalEquipmentAssignmentActions'
import { OperationalEquipmentDuplicatedAlert } from '../atoms/OperationalEquipmentDuplicatedAlert'
import { CarrierVisitAreaAutoComplete } from './CarrierVisitAreaAutoComplete'
import { OperationalEquipmentSharedAssignment } from './OperationalEquipmentSharedAssignment'

interface Props<T> {
  name: keyof T
  formStore: IFormStore<T>
  equipmentCount: number
  equipmentType?: EquipmentType
  initialSelectedAssignments: ICarrierVisitAssignment[]
}
export const OperationalEquipmentTrainAssignmentSubForm = observer(
  <T,>({
    name,
    equipmentCount,
    equipmentType,
    formStore,
    initialSelectedAssignments,
  }: Props<T>) => {
    const { t } = useTranslate()
    const store = useRegisterSubFormStore(formStore, name, new CarrierVisitAssignmentSubFormStore())

    useEffect(() => {
      store.setValues(initialSelectedAssignments, equipmentCount > 1)
    }, [initialSelectedAssignments, equipmentCount, store])

    return (
      <FieldBox
        label={t('trainAssignment', 'Train assignment')}
        labelIcon={<TrainIcon />}
        rightRender={
          <OperationalEquipmentAdd
            canAdd={store.canAddAssignment}
            onAdd={() => store.addAssignment()}
            dataCy='add-train-assignment'
          />
        }
        displayChildren={store.displayContent}
      >
        <Grid container spacing={2} alignItems='center'>
          <OperationalEquipmentSharedAssignment
            equipmentCount={equipmentCount}
            assignmentsTotal={store.visitIds.value.length}
            canAddAssignment={!store.assignments.value.length}
            equipmentType={equipmentType}
            assignmentType={t('trainVisits', 'Train visits')}
            onAdd={() => store.addAssignment()}
            onRemove={() => store.clearVisitIds()}
          />
          {store.assignments.value.map(
            (assignment: ICarrierVisitAssignment | null, index: number) =>
              assignment?.isDeleted !== true && (
                <Grid container size={12} key={`train-assignment-${index}`}>
                  <Grid size={10}>
                    <CarrierVisitAreaAutoComplete
                      dataCy={`train-assignment-${index}`}
                      label={t('train', 'Train')}
                      carrierType={CarrierType.Train}
                      selectedId={assignment?.visitId}
                      initialFilterName={assignment?.filter}
                      onChange={value =>
                        store.updateAssignment(
                          index,
                          value?.id,
                          value?.workAreas[0]?.id,
                          [],
                          value?.carrierName,
                        )
                      }
                    />
                  </Grid>

                  <OperationalEquipmentAssignmentActions
                    key={`train-assignment-actions-${index}`}
                    canAddAssignment={
                      index ===
                      store.assignments.value.reduce(
                        (lastIdx, a, i) => (a && a.isDeleted !== true ? i : lastIdx),
                        -1,
                      )
                    }
                    onAdd={() => store.addAssignment()}
                    onRemove={() => store.removeAssignment(index)}
                  />
                </Grid>
              ),
          )}

          <OperationalEquipmentDuplicatedAlert
            duplicatedAssignments={store.duplicatedAssignments}
            type='trains'
          />
        </Grid>
      </FieldBox>
    )
  },
)
